import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  STAGE_LABEL,
  START_STAGES,
  checkLegacyRedoNumber,
  cleanSerialNumber,
  type Kit,
  type Stage,
} from '@/domain';
import {
  previewDeliveryDate,
  useCatalog,
  useCreateJobOrder,
} from '@/application/usecases/useIntake';
import type { CreateJobOrderCommand } from '@/application/ports/JobOrderRepository';
import { useAddKitPhoto } from '@/application/usecases/useKits';
import { useSession } from '@/application/usecases/useSession';
import { openPhotoTile } from '@/features/photos/photoViewerStore';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { CloseIcon, LockIcon } from '@/ui/icons';
import { CaseFields } from './CaseFields';
import { RedoFields } from './RedoFields';
import {
  NO_FIELD_ERRORS,
  caseClients,
  caseRedo,
  caseSpans,
  caseTeeth,
  caseUnits,
  emptyCase,
  fieldErrorsFrom,
  mergeSpanErrors,
  patientProblem,
  spanErrorsFrom,
  withoutFieldError,
  spanRowErrors,
  type CaseFieldErrors,
  type CaseFormValue,
} from './caseForm';
import styles from './IntakeScreen.module.css';

function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * New job order. Sections 5.2 and 11.1.
 *
 * The fields are `CaseFields`, which the edit screen renders too (system test
 * handover 2.4); this screen owns what only intake does: the photographs, the
 * submit, and the created panel that offers the sheets to print.
 *
 * Creating without teeth or a count fails with the exact toast and scrolls the
 * chart into view, because on a phone the chart is usually off screen by the
 * time the user reaches the button.
 *
 * Once the form's own checks have passed, Create asks first, for the desk
 * roles: the request of 5 October 2026 (third), section 2. The question names
 * the doctor, the units, the bench the case opens at and the case a redo
 * remakes. Cancel leaves the form exactly as it was; CAD / CAM, who are not
 * asked, create at once as before.
 *
 * Since the request of 6 October 2026 (second): the doctor and the patient are
 * looked up as they are typed and travel as their records where one is chosen
 * (items 4 and 5), and a remake may name an original from before this system
 * by its old number (item 1). What the server refuses about any of those four
 * fields is said against the field as well as beside the button.
 *
 * Since the request of 6 October 2026 (third), item 3, a patient is one record
 * whichever doctors it has been to. A name that more than one patient has is
 * refused here until one of them is chosen, or it is said to be a different
 * person: the name alone would go to the oldest of them.
 */
/** Where "Send this case to" starts, on every new case. See the state below. */
const FORM_START_STAGE: Stage = 'cadcam';

export function IntakeScreen() {
  const { data: session } = useSession();
  const { data: catalog } = useCatalog();
  const create = useCreateJobOrder();
  const ask = useConfirmAction();
  const navigate = useNavigate();

  const chartRef = useRef<HTMLDivElement>(null);

  const canEditDates = session?.permissions.includes('editDates') ?? false;
  // Both are conveniences on the form, and the server decides either way.
  const canRedo = session?.permissions.includes('redo') ?? false;
  const canMarkUrgent = session?.permissions.includes('markUrgent') ?? false;
  const isCadcam = session?.role === 'cadcam';

  const [value, setValue] = useState<CaseFormValue>(() => {
    const today = todayIso();
    return emptyCase(today, previewDeliveryDate(today));
  });
  /*
   * Span messages the server returned, by row. The form's own checks run the
   * same rules, so this is the rare case where the two disagree; the row is
   * marked either way, and the mark goes as soon as the rows or the chart move.
   */
  const [serverSpanErrors, setServerSpanErrors] = useState<Record<number, string>>({});
  /*
   * What was refused about the doctor, the patient or the remake, each against
   * its own field: the server's words, or the form's own check of an old job
   * order number. Like the span messages, a mark goes as soon as the field it
   * is on is touched.
   */
  const [fieldErrors, setFieldErrors] = useState<CaseFieldErrors>(NO_FIELD_ERRORS);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ jobOrderNo: string; kits: Kit[] } | null>(null);

  /*
   * Photographs of the case as it arrived, held in the browser until the job
   * order exists. They cannot be uploaded before that: a photo belongs to a
   * kit and there is no kit until the order is created. If one upload fails
   * the order still stands.
   */
  const [photos, setPhotos] = useState<File[]>([]);
  const photoInput = useRef<HTMLInputElement>(null);
  const addPhoto = useAddKitPhoto();
  const [photoProblem, setPhotoProblem] = useState<string | null>(null);

  useEffect(() => {
    setServerSpanErrors({});
  }, [value.spanRows, value.teeth]);

  /*
   * Each mark goes when its OWN field is touched. One effect used to clear all
   * four when any of these moved, and two of them move by themselves: the names
   * typed for the doctor and the patient are looked up a moment later, and the
   * answer ties each to its record. An answer that arrived after Create was
   * pressed took the mark off the old job order number and left the message
   * beside the button. Found by the deploy workflow on 11 October 2026, where
   * the answer came later than it does on a desk.
   */
  useEffect(() => {
    setFieldErrors((current) => withoutFieldError(current, 'doctor'));
  }, [value.dentistName, value.doctorId]);
  useEffect(() => {
    setFieldErrors((current) => withoutFieldError(current, 'patient'));
  }, [value.patientName, value.patientId, value.newPatient]);
  useEffect(() => {
    setFieldErrors((current) => withoutFieldError(current, 'redoOf'));
  }, [value.redoOf, value.redoOfLegacy]);
  useEffect(() => {
    setFieldErrors((current) => withoutFieldError(current, 'redoReason'));
  }, [value.redoReason, value.redoOfLegacy]);

  /*
   * The bench this case opens at: CAD / CAM until somebody picks another, for
   * every case, digital or impression. The client's choice on 29 September
   * 2026, and their own example in section 4 of the request of 20 September.
   * It used to follow the route (Plaster for an impression), which reception
   * read as the system deciding for them. Reception changes it for the case
   * that needs another bench first; the case goes wherever the list says.
   */
  const [startStage, setStartStage] = useState<Stage>(FORM_START_STAGE);

  const units = caseUnits(value);
  const clientSpanErrors = spanRowErrors(value.spanRows, value.teeth);
  const spanErrors = mergeSpanErrors(clientSpanErrors, serverSpanErrors);

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // Checked here rather than left to the server's 400, so the message names
    // the field and the chart scrolls back into view on a phone.
    if (value.dentistName.trim() === '') {
      setError(strings.intake.dentistRequired);
      return;
    }
    if (value.patientName.trim() === '') {
      setError(strings.intake.patientRequired);
      return;
    }
    /*
     * A name more than one patient has, with none of them chosen. The request
     * of 6 October 2026 (third), item 3: sent as it is, the name would go to
     * the oldest of them without anybody deciding it. Said against the field,
     * which is where the list to choose from is.
     */
    const undecided = patientProblem(value);
    if (undecided !== null) {
      setError(undecided);
      setFieldErrors({ ...NO_FIELD_ERRORS, patient: undecided });
      return;
    }
    /*
     * A case needs teeth OR a count. Reception takes cases over the phone
     * knowing there are four crowns before knowing which four. Neither given
     * is still a refusal, because an order covering nothing is not an order.
     */
    if (units === 0) {
      setError(strings.intake.noUnits);
      toast(strings.intake.noUnits);
      // Guarded: an environment without scrollIntoView must still get the
      // message rather than have the submit handler throw underneath it.
      const chart = chartRef.current;
      if (typeof chart?.scrollIntoView === 'function') {
        chart.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    const firstSpanError = clientSpanErrors.find((message) => message !== null) ?? null;
    if (firstSpanError !== null) {
      setError(firstSpanError);
      return;
    }
    /*
     * A remake of a case from before this system names it by its old number,
     * typed by hand: one to thirty two characters. The request of 6 October
     * 2026 (second), item 1. Checked here in the server's own words, and said
     * against the field too, which is at the top of a long form.
     */
    const redo = caseRedo(value);
    const badNumber = redo?.redoOfLegacy === true ? checkLegacyRedoNumber(redo.redoOf) : null;
    if (badNumber !== null) {
      setError(badNumber);
      setFieldErrors({ ...NO_FIELD_ERRORS, redoOf: badNumber });
      return;
    }
    setError(null);

    const order: CreateJobOrderCommand = {
      // By its record where one was chosen, by its name where it is new.
      ...caseClients(value),
      origin: value.origin,
      input: value.input,
      product: value.product,
      teeth: caseTeeth(value),
      units,
      spans: caseSpans(value),
      shade: value.shade,
      specification: value.spec,
      itemsReceived: value.items,
      startDate: value.startDate,
      ...(canEditDates ? { deliveryDate: value.deliveryDate } : {}),
      instructions: value.instructions.trim(),
      // The number on the doctor's paper, when one was typed. The lab's request of 17 October 2026, item 7.
      ...(cleanSerialNumber(value.serialNumber) === null ? {} : { serialNumber: cleanSerialNumber(value.serialNumber) }),
      /*
       * A remake, and urgent from the moment it is booked in. Section 7 of
       * the request of 16 September 2026. Sent only when they are set, so a
       * case that is neither carries neither.
       */
      ...(redo ?? {}),
      ...(value.urgent ? { urgent: true } : {}),
      // The bench the case opens at. Always sent, so the server never has to
      // guess at what the form showed.
      startStage,
    };

    ask(
      strings.confirm.createCase(
        order.dentistName,
        units,
        STAGE_LABEL[startStage],
        redo?.redoOf ?? null,
        redo?.redoOfLegacy === true,
      ),
      () =>
        create.mutateAsync(order).then(
          (result) => {
            toast(`${result.jobOrder.jobOrderNo} ${strings.intake.created}`);
            setCreated({ jobOrderNo: result.jobOrder.jobOrderNo, kits: result.kits });
            // The next case starts on CAD / CAM again, as a fresh form does.
            setStartStage(FORM_START_STAGE);
            // The next case starts clean, keeping what a desk entering a run of
            // cases from one dentist sets once: the item, the specification
            // and the dates.
            setValue((current) => ({
              ...emptyCase(current.startDate, current.deliveryDate),
              product: current.product,
              spec: current.spec,
            }));
            void attachPhotos(result.kits[0]?.id);
          },
          // Handled here, on the form, so the question closes without a second word about it.
          (failure: Error) => {
            setError(failureWords(failure));
            setServerSpanErrors(spanErrorsFrom(failure));
            setFieldErrors(fieldErrorsFrom(failure));
          },
        ),
      { confirmLabel: strings.intake.create },
    );
  }

  /**
   * Sends the held photographs to the kit the order created.
   *
   * Sequential rather than concurrent: these are phone photographs on lab wifi
   * and firing six multipart uploads at once is how a handset stalls. The
   * order is already saved by this point, so a slow strip costs nothing.
   */
  async function attachPhotos(kitId: string | undefined) {
    if (kitId === undefined || photos.length === 0) return;

    let failed = 0;
    for (const file of photos) {
      try {
        await addPhoto.mutateAsync({ kitId, file });
      } catch {
        failed += 1;
      }
    }

    setPhotos([]);
    setPhotoProblem(failed === 0 ? null : `${failed} ${strings.intake.photosFailed}`);
    if (failed > 0) toast(`${failed} ${strings.intake.photosFailed}`);
  }

  if (created !== null) {
    return (
      <div>
        <div className={styles.created}>
          <h3>
            {created.jobOrderNo} {strings.intake.created}
          </h3>
          <p>
            {created.kits.length}{' '}
            {created.kits.length === 1 ? strings.intake.units : `${strings.intake.units}`}
          </p>

          {/*
            Said on the screen as well as in a toast. A toast disappears, and
            a reception desk that thought it had attached six photographs
            should be able to see, after the fact, that two of them did not go.
          */}
          {photoProblem !== null && <p className={styles.problem}>{photoProblem}</p>}
          <div className={styles.createdKits}>
            {created.kits.map((kit) => (
              <Button key={kit.id} onClick={() => void navigate(`/print/${kit.id}`)}>
                {strings.intake.printSheets} · {kit.id}
              </Button>
            ))}
            <Button variant="primary" onClick={() => setCreated(null)}>
              {strings.intake.title}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.intake.label}</span>
        <h2>{strings.intake.title}</h2>
        <span className={styles.hint}>
          {isCadcam ? strings.intake.hintCadcam : strings.intake.hintOther}
        </span>
      </div>

      <form className={styles.card} onSubmit={submit} noValidate>
        {/*
          Near the top, where a decision about the whole case belongs, and in
          the order the client described it: choose redo, find the case, then
          fill the form. Section 7.4 of the request of 16 September 2026.
        */}
        {canRedo && (
          <RedoFields
            value={{
              redoOf: value.redoOf,
              redoOfLegacy: value.redoOfLegacy,
              redoReason: value.redoReason,
            }}
            onChange={(next) => setValue((current) => ({ ...current, ...next }))}
            errors={fieldErrors}
          />
        )}

        {canMarkUrgent && (
          <label className={styles.urgent}>
            <input
              type="checkbox"
              checked={value.urgent}
              onChange={(event) =>
                setValue((current) => ({ ...current, urgent: event.target.checked }))
              }
            />
            <span>
              {strings.redo.urgent}
              <small>{strings.redo.urgentHint}</small>
            </span>
          </label>
        )}

        <CaseFields
          value={value}
          onChange={setValue}
          catalogItems={catalog?.items ?? []}
          dates={{ canEditDates, isCadcam }}
          identity
          identityErrors={fieldErrors}
          spanErrors={spanErrors}
          chartRef={chartRef}
        />

        {/*
          Photographs of the case as it arrived. Queued here and sent once the
          order exists; whoever scans the tray sees them on the job view.
        */}
        <div className={`${styles.row} ${styles.one}`}>
          <div className={styles.field}>
            <span className={styles.l}>{strings.intake.photos}</span>
            <p className={styles.note}>{strings.intake.photosHint}</p>

            <input
              ref={photoInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              aria-label={strings.intake.photosInputLabel}
              className={styles.fileInput}
              onChange={(event) => {
                const picked = [...(event.target.files ?? [])];
                if (picked.length > 0) setPhotos((held) => [...held, ...picked]);
                // Cleared so picking the same file twice still fires a change.
                event.target.value = '';
              }}
            />

            {photos.length > 0 && (
              <ul className={styles.photoStrip} data-photo-tiles="">
                {photos.map((file, index) => (
                  <li key={`${file.name}-${String(index)}`} className={styles.photoChip}>
                    {/*
                      Opens in the image viewer, from the file on the device:
                      nothing has been sent yet. Removing it is the button
                      beside this one. The request of 5 October 2026 (third),
                      section 6.
                    */}
                    <button
                      type="button"
                      className={styles.photoOpen}
                      aria-label={strings.viewer.open(file.name)}
                      data-photo-tile={queuedPhotoId(file, index)}
                      onClick={(event) =>
                        openPhotoTile(
                          event,
                          photos.map((each, at) => ({
                            kind: 'local',
                            id: queuedPhotoId(each, at),
                            file: each,
                            caption: strings.viewer.notSentYet,
                          })),
                          index,
                        )
                      }
                    >
                      <PhotoThumb file={file} />
                    </button>
                    <button
                      type="button"
                      className={styles.photoDrop}
                      aria-label={`${strings.intake.photosRemove} ${file.name}`}
                      onClick={() => setPhotos((held) => held.filter((_, i) => i !== index))}
                    >
                      <CloseIcon />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <Button type="button" onClick={() => photoInput.current?.click()}>
              {photos.length === 0 ? strings.intake.photosAdd : strings.intake.photosAddMore}
            </Button>
          </div>
        </div>

        {/*
          The bench the case opens at, the LAST row before Create, as section 4
          of the request of 20 September 2026 asks.

          Preselected with CAD / CAM, so reception sees where the case is going
          before they send it rather than after. Every case used to be created
          at Intake, where reception could not work it and CAD / CAM could not
          see it.

          Just the benches, with no hint under the list and no "usual" label on
          an option: the case goes where the list says, and Intake only when
          Intake is chosen. The client's request of 29 September 2026.
        */}
        <div className={`${styles.row} ${styles.one}`}>
          <div className={styles.field}>
            <label className={styles.l} htmlFor="intake-start-stage">
              {strings.intake.startStage}
            </label>
            <select
              id="intake-start-stage"
              name="startStage"
              className={styles.select}
              value={startStage}
              onChange={(event) => setStartStage(event.target.value as Stage)}
            >
              {START_STAGES.map((stage) => (
                <option key={stage} value={stage}>
                  {STAGE_LABEL[stage]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            onClick={() => {
              setValue((current) => ({ ...current, teeth: [], spanRows: [] }));
              setError(null);
            }}
          >
            {strings.intake.clear}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? strings.intake.creating : strings.intake.create}
          </Button>
        </div>

        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.footnote}>
          <LockIcon /> {strings.intake.footnote}
        </div>
      </form>
    </div>
  );
}

/**
 * A preview of a file that has not been uploaded yet.
 *
 * The object URL is revoked when the thumbnail goes away. Without that, a
 * reception desk that opens twenty cases in a morning leaks every image it
 * previewed until the tab is closed.
 */
/** A queued photograph has no id until it is sent: its name and its place in the queue stand in. */
function queuedPhotoId(file: File, index: number): string {
  return `${file.name}-${String(index)}`;
}

function PhotoThumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return url === null ? (
    <span className={styles.photoBlank} data-photo-frame="" />
  ) : (
    <img src={url} alt="" draggable={false} data-photo-frame="" />
  );
}
